import LabelInput from 'components/common/LabelInput';
import {ICountryArray} from 'data/country';
import {checkAuthCode, sendAuthCode} from 'function/api';
import {IModalState, IRegisterInfo} from 'pages/Register';
import React, {useCallback, useEffect, useState} from 'react';
import CountDown from 'components/common/CountDown';
import PhoneNumberInput from 'components/common/PhoneNumberInput';
import AuthSendButton from './AuthSendButton';
import {isValidatePhoneNumber} from 'function/validate';
import styled from 'styled-components';
import {colors} from 'styles/colors';
import {minmax} from 'utils/minmax';
import DefaultText from 'components/common/DefaultText';

/**
 * 전화번호 인증 컴포넌트의 Props 인터페이스
 * @property {ICountryArray} selectCountry - 선택된 국가
 * @property {React.Dispatch<React.SetStateAction<ICountryArray>>} setSelectCountry - 국가 설정 함수
 * @property {IRegisterInfo} registerInfo - 등록 정보
 * @property {React.Dispatch<React.SetStateAction<IRegisterInfo>>} setRegisterInfo - 등록 정보 설정 함수
 * @property {React.Dispatch<React.SetStateAction<boolean>>} setIsLoading - 로딩 상태 설정 함수
 * @property {React.Dispatch<React.SetStateAction<{}>>} setErrorModal - 에러 모달 설정 함수
 * @property {React.Dispatch<React.SetStateAction<boolean>>} setEnableButton - 버튼 활성화 상태 설정 함수
 * @property {React.Dispatch<React.SetStateAction<string>>} setAuthSendButtonTitle - 인증 전송 버튼 제목 설정 함수
 * @property {string} authSendButtonTitle - 인증 전송 버튼 제목
 * @property {'SIGN_UP' | 'RESET_PASSWORD'} codePurpose - 코드 목적
 * @property {React.Dispatch<React.SetStateAction<IModalState>>} setModalState - 모달 상태 설정 함수
 * @property {React.Dispatch<React.SetStateAction<boolean>>} setCheckedAuth - 인증 확인 상태 설정 함수
 * @property {boolean} checkedAuth - 인증 확인 여부
 * @property {string} [phoneNumberLabel] - 전화번호 라벨
 */
interface props {
  selectCountry: ICountryArray;
  setSelectCountry: React.Dispatch<React.SetStateAction<ICountryArray>>;
  registerInfo: IRegisterInfo;
  setRegisterInfo: React.Dispatch<React.SetStateAction<IRegisterInfo>>;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setErrorModal: React.Dispatch<React.SetStateAction<{}>>;
  setEnableButton: React.Dispatch<React.SetStateAction<boolean>>;
  setAuthSendButtonTitle: React.Dispatch<React.SetStateAction<string>>;
  authSendButtonTitle: string;
  codePurpose: 'SIGN_UP' | 'RESET_PASSWORD';
  setModalState: React.Dispatch<React.SetStateAction<IModalState>>;
  setCheckedAuth: React.Dispatch<React.SetStateAction<boolean>>;
  checkedAuth: boolean;
  phoneNumberLabel?: string;
}

/**
 * 전화번호 인증 컴포넌트
 * 전화번호를 입력하고 인증 코드를 받아 인증하는 컴포넌트입니다.
 * @param {props} props - 전화번호 인증 컴포넌트의 props
 * @returns {JSX.Element} 전화번호 인증 컴포넌트
 */
export default function PhoneVerification({
  selectCountry,
  setSelectCountry,
  registerInfo,
  setRegisterInfo,
  setIsLoading,
  setErrorModal,
  setEnableButton,
  setAuthSendButtonTitle,
  authSendButtonTitle,
  codePurpose,
  setModalState,
  setCheckedAuth,
  checkedAuth,
  phoneNumberLabel,
}: props) {
  // 사용 가능한 휴대폰인지 확인
  const [isAuthedPhone, setIsAuthedPhone] = useState(false);
  useEffect(() => {
    const authed = isValidatePhoneNumber(
      registerInfo.phone,
      selectCountry.languageCode,
    );
    setIsAuthedPhone(authed);
  }, [registerInfo.phone, selectCountry]);

  // 인증 코드 전송 여부
  const [isSendAuth, setIsSendAuth] = useState<boolean>(false);

  // 에러 메시지
  const [errorTexts, setErrorTexts] = useState({
    phone: '',
    auth: '',
  });

  // 타이머
  const [timer, setTimer] = useState(false);

  // 타이머 종료 함수
  const timerOver = useCallback(() => {
    setRegisterInfo({...registerInfo, phone: '', verifiCode: ''});
    setTimer(false);
    setAuthSendButtonTitle('sendAuth');
  }, []);

  // 타이머 재시작
  const [reStart, setReStart] = useState(0);

  useEffect(() => {
    // 에러 메시지
    const errorMsg =
      registerInfo.verifiCode.length === 5 && !checkedAuth ? 'notAuth' : '';
    setErrorTexts({auth: errorMsg, phone: ''});

    // 인증 코드 체크
    const ayncFunc = async () => {
      if (registerInfo.verifiCode.length > 4) {
        const check = await checkAuthCode({
          purpose: codePurpose,
          code: registerInfo.verifiCode,
          phone: registerInfo.phone,
          setIsLoading,
        });
        setCheckedAuth(check);
        setEnableButton(check);

        const errorMsg =
          registerInfo.verifiCode.length === 5 && check ? '' : 'notAuth';
        setErrorTexts({...errorTexts, auth: errorMsg});
        if (errorMsg === '') setTimer(false);
      } else {
        setCheckedAuth(false);
        setEnableButton(false);
      }
    };
    ayncFunc();
  }, [registerInfo]);

  return (
    <>
      <div>
        {/* 휴대폰번호 입력란 */}
        <PhoneNumberInput
          label={phoneNumberLabel ? phoneNumberLabel : 'phoneNumberId'}
          selectCountry={selectCountry}
          setSelectCountry={setSelectCountry}
          phoneNumber={registerInfo.phone}
          setPhoneNumber={(e: any) => {
            setRegisterInfo({...registerInfo, phone: e});
            setAuthSendButtonTitle('sendAuth');
          }}
          onClickInput={() => {
            if (isSendAuth) {
              setTimer(false);
              setIsSendAuth(false);
              setRegisterInfo({...registerInfo, phone: '', verifiCode: ''});
              setTimer(false);
              setAuthSendButtonTitle('sendAuth');
            }
          }}
          timer={
            // 타이머
            timer ? (
              <CountDown
                until={5}
                onFinish={timerOver}
                style={{
                  width: '100%',
                  textAlign: 'end',
                  marginTop: '10px',
                }}
                reStart={reStart}
              />
            ) : null
          }
          sendAuth={
            // 인증번호 전송 버튼
            <AuthSendButton
              authSendButtonTitle={authSendButtonTitle}
              authed={isAuthedPhone}
              onclick={async () => {
                if (authSendButtonTitle === 'reSendAuth') {
                  setRegisterInfo(prev => ({...prev, verifiCode: ''}));
                  setCheckedAuth(false);
                  setReStart(prev => prev + 1);
                }
                if (isAuthedPhone) {
                  const authMessage = await sendAuthCode({
                    purpose: codePurpose,
                    locale: selectCountry.languageCode,
                    phone: registerInfo.phone,
                    setIsLoading,
                    setErrorModal,
                    setModalState,
                  });
                  if (authMessage === true) {
                    setAuthSendButtonTitle('reSendAuth');
                    setTimer(true);
                    setIsSendAuth(true);
                  } else if (
                    authMessage.state === 'SIGNATURE_INITIALIZED_AND_COMPLETED'
                  ) {
                    // 이미 가입된 계정
                    setErrorTexts({...errorTexts, phone: 'usedContact'});
                  } else if (authMessage !== false) {
                    // 작성 중인 화면 이동 모달 오픈
                    setModalState
                      ? setModalState(prev => ({
                          ...prev,
                          tempInfo: {state: authMessage.state, open: true},
                        }))
                      : null;
                  }
                }
              }}
            />
          }
          errorBorder={errorTexts.phone !== '' ? true : false}
        />
        {errorTexts && (
          <ErrorTextStyle>
            <DefaultText
              text={errorTexts.phone}
              textStyle={{fontSize: minmax(16), color: colors.primary}}
            />
          </ErrorTextStyle>
        )}
      </div>

      {/* 인증번호 입력란*/}
      <LabelInput
        labelText="authNumber"
        type="number"
        value={registerInfo.verifiCode}
        setValue={(e: any) =>
          e.length < 6
            ? setRegisterInfo({...registerInfo, verifiCode: e})
            : null
        }
        isColumn
        rightImg={checkedAuth ? 'images/common/check.png' : ''}
        disabled={checkedAuth}
        errorText={errorTexts.auth}
      />
    </>
  );
}

const ErrorTextStyle = styled.div`
  width: 100%;
  margin-top: 8px;
  text-align: right;
  padding-right: ${minmax(160)};
  font-size: max(10px, min(1.5vw, 16px));
  color: ${colors.primary};
`;
